import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';
import AuthContext from '../context/AuthContext';
import Spinner from '../components/Spinner';
import { toast } from 'react-toastify';
import { FiUser, FiPhone, FiDollarSign, FiShoppingCart, FiCreditCard, FiArrowLeft, FiCalendar, FiAward, FiActivity, FiClock, FiMessageCircle, FiGift } from 'react-icons/fi';

const ClientDetail = () => {
  const { id } = useParams();
  const [clientData, setClientData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    const fetchClientDetail = async () => {
      if (!user || (user.plan !== 'pro' && user.plan !== 'premium')) {
        setLoading(false);
        return;
      }
      try {
        const response = await api.get(`/clients/${id}`);
        setClientData(response.data);
      } catch (error) {
        toast.error("Impossible de charger les détails du client.");
      } finally {
        setLoading(false);
      }
    };

    fetchClientDetail();
  }, [id, user]);

  if (loading) {
    return <Spinner message="Chargement des détails du client..." />;
  }

  if (!clientData) {
    return <div className="text-center text-red-500">Données du client non trouvées ou accès non autorisé.</div>;
  }

  const { client, stats, salesHistory, activeDebts } = clientData;

  // Logique de profilage client
  let clientStatus = "Nouveau";
  let clientColor = "text-gray-500 bg-gray-100";
  let advice = "Encouragez ce client à revenir pour le fidéliser.";

  if (stats.totalSpent > 100000) {
      clientStatus = "VIP";
      clientColor = "text-yellow-700 bg-yellow-100";
      advice = "C'est un excellent client ! Pensez à lui offrir un petit cadeau ou une remise.";
  } else if (salesHistory.length > 5) {
      clientStatus = "Régulier";
      clientColor = "text-blue-700 bg-blue-100";
      advice = "Un client fidèle. Informez-le de vos nouveautés en priorité.";
  }

  // Calcul des points de fidélité (1 point pour 1000 FCFA)
  const loyaltyPoints = Math.floor((stats.totalSpent || 0) / 1000);

  // Assistant : Détection de client dormant (Churn)
  const lastPurchaseDate = salesHistory?.length > 0 ? new Date(salesHistory[0].date) : null;
  const daysSinceLastPurchase = lastPurchaseDate ? Math.floor((new Date() - lastPurchaseDate) / (1000 * 60 * 60 * 24)) : null;
  const isDormant = daysSinceLastPurchase > 30;

  const handleWakeUpClient = () => {
    if (!client.phone) {
        toast.error("Pas de numéro de téléphone pour ce client.");
        return;
    }
    const shopName = user?.shopName || 'Notre Boutique';
    const message = `Bonjour ${client.name} ! 👋%0A%0ACela fait un moment qu'on ne vous a pas vu chez ${shopName}.%0AJ'espère que vous allez bien ! N'hésitez pas à passer voir nos nouveautés.%0A%0AÀ très vite !`;
    const phoneNumber = client.phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  return (
    <div>
      <Link to="/dashboard/clients" className="inline-flex items-center text-gray-500 hover:text-brand-blue transition-colors mb-6 group">
        <div className="w-10 h-10 rounded-full bg-white border border-gray-200 group-hover:border-brand-blue group-hover:bg-blue-50 flex items-center justify-center mr-3 shadow-sm transition-all">
          <FiArrowLeft className="w-5 h-5" />
        </div>
        <span className="font-medium">Retour à la liste des clients</span>
      </Link>
      
      {/* Header */}
      <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row justify-between items-start md:items-center">
        <div className="flex items-center">
          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mr-4">
            <FiUser className="w-8 h-8 text-gray-500" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{client.name}</h2>
            <p className="text-gray-500">{client.phone && <span className="flex items-center"><FiPhone className="mr-1" size={14}/> {client.phone}</span>}</p>
          </div>
        </div>
        <div className="mt-4 md:mt-0 flex flex-col items-end">
            {isDormant && (
                <div className="mb-3 flex items-center bg-orange-100 text-orange-800 px-3 py-1 rounded-lg text-sm border border-orange-200">
                    <FiClock className="mr-2" />
                    <span className="mr-2">Absent depuis {daysSinceLastPurchase} jours</span>
                    <button 
                        onClick={handleWakeUpClient}
                        className="bg-white text-orange-600 hover:text-orange-800 font-bold px-2 py-0.5 rounded shadow-sm text-xs flex items-center transition-colors"
                        title="Envoyer un message de courtoisie"
                    >
                        <FiMessageCircle className="mr-1"/> Faire un coucou
                    </button>
                </div>
            )}
            <span className={`px-3 py-1 rounded-full text-sm font-bold flex items-center ${clientColor}`}>
                <FiAward className="mr-2" /> {clientStatus}
            </span>
            <p className="text-xs text-gray-500 mt-2 max-w-xs text-right italic">
                <FiActivity className="inline mr-1"/> {advice}
            </p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-blue-50 p-6 rounded-2xl flex items-center gap-4 border border-blue-100">
          <div className="bg-blue-200 p-3 rounded-full"><FiDollarSign className="h-6 w-6 text-blue-800" /></div>
          <div>
            <p className="text-sm text-blue-700">Total dépensé</p>
            <p className="font-bold text-2xl text-blue-900">{(stats.totalSpent || 0).toLocaleString('fr-FR')} CFA</p>
          </div>
        </div>
        <div className="bg-green-50 p-6 rounded-2xl flex items-center gap-4 border border-green-100">
          <div className="bg-green-200 p-3 rounded-full"><FiShoppingCart className="h-6 w-6 text-green-800" /></div>
          <div>
            <p className="text-sm text-green-700">Nombre d'achats</p>
            <p className="font-bold text-2xl text-green-900">{salesHistory?.length || 0}</p>
          </div>
        </div>
        <div className="bg-red-50 p-6 rounded-2xl flex items-center gap-4 border border-red-100">
          <div className="bg-red-200 p-3 rounded-full"><FiCreditCard className="h-6 w-6 text-red-800" /></div>
          <div>
            <p className="text-sm text-red-700">Dettes en cours</p>
            <p className="font-bold text-2xl text-red-900">{activeDebts?.length || 0}</p>
          </div>
        </div>
        <div className="bg-purple-50 p-6 rounded-2xl flex items-center gap-4 border border-purple-100">
          <div className="bg-purple-200 p-3 rounded-full"><FiGift className="h-6 w-6 text-purple-800" /></div>
          <div>
            <p className="text-sm text-purple-700">Points Fidélité</p>
            <p className="font-bold text-2xl text-purple-900">{loyaltyPoints}</p>
          </div>
        </div>
      </div>

      {/* Sales History & Active Debts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Sales History */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center"><FiShoppingCart className="mr-2"/>Historique des Achats</h3>
          <div className="max-h-96 overflow-y-auto">
            {salesHistory?.length > 0 ? (
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-500 uppercase bg-gray-50"><tr><th className="py-3 px-4">Date</th><th className="py-3 px-4">Articles</th><th className="py-3 px-4 text-right">Total</th></tr></thead>
                <tbody>
                  {salesHistory.map(sale => (
                    <tr key={sale._id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium text-gray-900">{new Date(sale.date).toLocaleDateString('fr-FR')}</td>
                      <td className="py-3 px-4">{sale.items.length}</td>
                      <td className="py-3 px-4 text-right font-semibold text-brand-blue">{sale.totalAmount.toLocaleString('fr-FR')} CFA</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="text-center text-gray-500 py-8">Aucun achat enregistré.</p>}
          </div>
        </div>
        {/* Active Debts */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center"><FiCreditCard className="mr-2"/>Dettes en Cours</h3>
          <div className="max-h-96 overflow-y-auto">
            {activeDebts?.length > 0 ? (
              <table className="w-full text-sm text-left">
                <thead className="text-xs text-gray-500 uppercase bg-gray-50"><tr><th className="py-3 px-4">Montant Restant</th><th className="py-3 px-4">Échéance</th><th className="py-3 px-4"></th></tr></thead>
                <tbody>
                  {activeDebts.map(debt => (
                    <tr key={debt._id} className="border-b hover:bg-red-50">
                      <td className="py-3 px-4 font-bold text-red-700">{debt.amountRemaining.toLocaleString('fr-FR')} CFA</td>
                      <td className="py-3 px-4 text-gray-600">{new Date(debt.dueDate).toLocaleDateString('fr-FR')}</td>
                      <td className="py-3 px-4 text-right">
                        <Link to={`/dashboard/debts?clientId=${client._id}`} className="text-sm font-bold text-brand-blue hover:underline">Gérer</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <p className="text-center text-gray-500 py-8">Aucune dette en cours.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDetail;